import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { FinancialRecurringTransaction } from '../../../Interfaces/financialRecurring'

interface MarkAsPaidModalProps {
  isOpen: boolean
  onClose: () => void
  transaction?: FinancialRecurringTransaction | null
  isSubmitting?: boolean
  paymentMethodOptions?: { label: string; value: number }[]
  paymentConditionOptions?: { label: string; value: number }[]
  onSubmit: (payload: {
    id: number
    payment_date: string
    interest_value?: number
    paid_value?: number
    payment_method_id?: number | null
    payment_condition_id?: number | null
  }) => Promise<void>
}

const todayISO = () => format(new Date(), 'yyyy-MM-dd')

export function MarkAsPaidModal({
  isOpen,
  onClose,
  transaction,
  isSubmitting,
  paymentMethodOptions = [],
  paymentConditionOptions = [],
  onSubmit,
}: MarkAsPaidModalProps) {
  const [paymentDate, setPaymentDate] = useState<string>(todayISO())
  const [interestValue, setInterestValue] = useState<string>('0')
  const [paidValue, setPaidValue] = useState<string>('')
  const [paymentMethodId, setPaymentMethodId] = useState<string>('')
  const [paymentConditionId, setPaymentConditionId] = useState<string>('')

  const isReceita = transaction?.type === 'CREDITO' || transaction?.type === 'BOLETO'

  useEffect(() => {
    if (isOpen && transaction) {
      setPaymentDate(transaction.payment_date?.slice(0, 10) ?? todayISO())
      setInterestValue(transaction.interest_value ? String(transaction.interest_value) : '0')
      const defaultPaid =
        transaction.paid_value && transaction.paid_value > 0
          ? String(transaction.paid_value)
          : String(transaction.value + (transaction.interest_value ?? 0))
      setPaidValue(defaultPaid)
      
      // Preencher forma e condição de pagamento se já estiverem informadas
      setPaymentMethodId(transaction.payment_method_id ? String(transaction.payment_method_id) : '')
      setPaymentConditionId(transaction.payment_condition_id ? String(transaction.payment_condition_id) : '')
    }
  }, [isOpen, transaction])

  const handleSubmit = async () => {
    if (!transaction) return

    const transactionId = transaction.recurring_transaction_id ?? transaction.id

    await onSubmit({
      id: transactionId,
      payment_date: paymentDate,
      interest_value: Number(interestValue) || 0,
      paid_value: Number(paidValue) || undefined,
      payment_method_id: paymentMethodId ? Number(paymentMethodId) : null,
      payment_condition_id: paymentConditionId ? Number(paymentConditionId) : null,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg="gray.800">
        <ModalHeader>{isReceita ? 'Marcar como recebido' : 'Marcar como pago'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel>{isReceita ? 'Data de recebimento' : 'Data de pagamento'}</FormLabel>
            <Input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Juros/Multa</FormLabel>
            <Input
              type="number"
              step="0.01"
              value={interestValue}
              onChange={(event) => setInterestValue(event.target.value)}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>{isReceita ? 'Valor recebido' : 'Valor pago'}</FormLabel>
            <Input type="number" step="0.01" value={paidValue} onChange={(event) => setPaidValue(event.target.value)} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Forma de Pagamento</FormLabel>
            <Select
              bg="gray.700"
              color="white"
              borderColor="gray.600"
              _hover={{ borderColor: 'pink.400' }}
              _focus={{ borderColor: 'pink.400', boxShadow: 'none', color: 'white' }}
              value={paymentMethodId}
              onChange={(event) => setPaymentMethodId(event.target.value)}
              placeholder="Selecione (opcional)">
              {paymentMethodOptions.map((option) => (
                <option key={option.value} value={option.value} style={{ color: 'black', backgroundColor: 'white' }}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Condição de Pagamento</FormLabel>
            <Select
              bg="gray.700"
              color="white"
              borderColor="gray.600"
              _hover={{ borderColor: 'pink.400' }}
              _focus={{ borderColor: 'pink.400', boxShadow: 'none', color: 'white' }}
              value={paymentConditionId}
              onChange={(event) => setPaymentConditionId(event.target.value)}
              placeholder="Selecione (opcional)">
              {paymentConditionOptions.map((option) => (
                <option key={option.value} value={option.value} style={{ color: 'black', backgroundColor: 'white' }}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="green" onClick={handleSubmit} isLoading={isSubmitting}>
            {isReceita ? 'Confirmar recebimento' : 'Confirmar pagamento'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
